/**
 * Agent API payloads → domain models. Keeps the nullable API surface out of the
 * control panel: screens receive empty strings instead of nulls.
 */
import type {
  AdminAgent,
  AdminAgentDetail,
  AgentDetails,
  AgentPersona,
  AgentResponse,
  PersonaResponse,
} from './agentAdminTypes';

const text = (value: string | null | undefined): string => value ?? '';

const count = (value: number | null | undefined): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const joinName = (
  first: string | null | undefined,
  last: string | null | undefined,
): string => [text(first), text(last)].filter(Boolean).join(' ');

/**
 * Body stats stay nullable: an agent with no weight recorded must render as
 * "not set" rather than 0 kg.
 */
const measure = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export const mapAdminAgent = (item: AgentResponse): AdminAgent => ({
  agentId: count(item.agentId),
  agentGuid: text(item.agentGuid),
  userName: text(item.userName),
  email: text(item.agentMail),
  fullName: joinName(item.firstName, item.lastName),
  photo: text(item.agentPhoto),
  persona: text(item.personaName),
  tone: text(item.tone),
  motivationStyle: text(item.motivationStyle),
  bio: text(item.bio),
  likeFrequency: count(item.likeFrequencyPerDay),
  commentFrequency: count(item.commentFrequencyPerDay),
  totalPosts: count(item.totalPosts),
  weeklyPosts: count(item.weeklyPosts),
  weeklyLikes: count(item.weeklyLikes),
  weeklyComments: count(item.weeklyComments),
  buddiesCount: count(item.buddiesCount),
  joiningDate: text(item.joiningDate ?? item.createdAt),
  firstPostDate: text(item.firstPostDate),
  lastPostDate: text(item.lastPostDate),
  isActive: item.isActive === true,
  isEmailVerified: item.isEmailVerified === true,
  gender: text(item.gender),
  country: text(item.country),
  currentWeight: measure(item.currentWeight),
  weightUnit: text(item.scaleUnitWeightName),
  currentHeight: measure(item.currentHeight),
  heightUnit: text(item.scaleUnitHeightName),
});

export const mapAdminAgentDetail = (item: AgentDetails): AdminAgentDetail => ({
  agentId: count(item.agentId),
  agentGuid: text(item.agentGuid),
  userName: text(item.userName),
  email: text(item.email),
  fullName: joinName(item.firstName, item.lastName),
  photo: text(item.picture),
  persona: text(item.personaType),
  tone: text(item.tone),
  motivationStyle: text(item.motivationStyle),
  bio: text(item.aboutMe),
  likeFrequency: count(item.likeFrequency),
  commentFrequency: count(item.commentFrequency),
  // The details endpoint reports the profile, not the activity counters the
  // list carries, so they stay zero here rather than showing stale numbers.
  totalPosts: 0,
  weeklyPosts: 0,
  weeklyLikes: 0,
  weeklyComments: 0,
  buddiesCount: 0,
  joiningDate: text(item.createdAt),
  firstPostDate: '',
  lastPostDate: '',
  isActive: item.isActive === true,
  // The details endpoint carries no verification flag; the list does.
  isEmailVerified: false,
  gender: text(item.genderName),
  country: text(item.country),
  currentWeight: measure(item.currentWeight),
  weightUnit: text(item.unitWeightName),
  currentHeight: measure(item.currentHeight),
  heightUnit: text(item.scaleUnitHeightName),
  personaId: count(item.personaTypeId),
  firstName: text(item.firstName),
  lastName: text(item.lastName),
  mobileNo: text(item.mobileNo),
  dateOfBirth: text(item.dob),
  startWeight: measure(item.startWeight),
  goalWeight: measure(item.goalWeight),
  weightUnitId: measure(item.unitWeight),
});

export const mapAgentPersona = (item: PersonaResponse): AgentPersona => ({
  id: count(item.agentPersonaId),
  name: text(item.personaName),
});
